import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Share2, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../config/translations';

/**
 * Share button for product pages.
 *
 * Uses the native Web Share API (navigator.share) when available — typically on
 * mobile browsers and HTTPS desktop — and falls back to copying the URL to the
 * clipboard with a transient "copied" confirmation.
 *
 * Both paths are feature-detected and wrapped so the button degrades gracefully
 * on older browsers (execCommand legacy fallback) and never throws.
 */
function ShareButton({ url, title, text }) {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  // Clear the "copied" reset timer if the button unmounts mid-countdown.
  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');

  const handleShare = useCallback(async () => {
    const shareData = {
      title: title || (typeof document !== 'undefined' ? document.title : ''),
      text: text || '',
      url: shareUrl,
    };

    // Prefer the native Web Share API when the browser supports it.
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        // AbortError means the user dismissed the share sheet — do nothing.
        // Any other error falls through to the clipboard fallback.
        if (err && err.name === 'AbortError') return;
      }
    }

    // Fallback: copy the link to the clipboard.
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else if (typeof document !== 'undefined') {
        // Legacy fallback for browsers without the async Clipboard API.
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      } else {
        return; // No clipboard mechanism available.
      }

      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently ignore clipboard failures — sharing is a non-critical enhancement.
    }
  }, [shareUrl, title, text]);

  return (
    <button
      type="button"
      onClick={handleShare}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-primary-300 hover:text-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 min-h-[40px]"
      aria-label={copied ? t('linkCopied') : t('share')}
      title={copied ? t('linkCopied') : t('share')}
      aria-live="polite"
    >
      {copied ? (
        <>
          <Check className="w-4 h-4 text-green-600" aria-hidden="true" />
          <span>{t('linkCopied')}</span>
        </>
      ) : (
        <>
          <Share2 className="w-4 h-4" aria-hidden="true" />
          <span>{t('share')}</span>
        </>
      )}
    </button>
  );
}

export default memo(ShareButton);
